import type { NotificationStepConfig } from '../../services/tenantApi';
import { parseJsonObject } from '../../services/inboundSchemasApi';

export type ContentSource = 'Alert' | 'Notification';
export interface ContentStep { stepType: string; stepOrder: number; config: Record<string, unknown> }
export interface ContentVariable { path: string; category: 'Common' | 'Alert' | 'Notification' | 'Integration' | 'Global' | 'Payload' | 'Steps'; note?: string }
export const validVariablePath = (path: string) => /^[a-zA-Z0-9_.-]+$/.test(path) && !/^secret(?:\.|$)/i.test(path);
const fields = ['id', 'title', 'description', 'severity', 'status', 'service', 'source', 'assignee', 'externallink',
  'externalalertid', 'externalticketid', 'correlationid', 'resolution', 'occurredat', 'createdat', 'tenantid', 'inboundintegrationid'];
const outputs: Record<string, string[]> = {
  Notification: ['notified', 'attempted', 'adHocEmails', 'errors'],
  Webhook: ['response', 'responseBody', 'responseStatusCode', 'attempts', 'success'],
  IntegrationAction: ['actionKey', 'ticketId', 'responseStatusCode', 'responseBody', 'success', 'idempotencyKey', 'createSkipped', 'createSkipReason'],
  Ping: ['target', 'packetCount', 'successReplies', 'reply', 'timeout', 'success', 'responseBody', 'error'],
  UpdateAlert: ['updated'], Escalate: ['escalateAction'], Condition: ['conditionMet'],
};

export function contentCatalog(source: ContentSource, steps: ContentStep[], order: number, payloadPaths: string[] = []): ContentVariable[] {
  const sourceRoot = source.toLowerCase();
  const entries: ContentVariable[] = fields.flatMap(field => [
    { path: `${sourceRoot}.${field}`, category: source as 'Alert' | 'Notification' },
    { path: `event.${field}`, category: 'Common' as const },
  ]);
  entries.push({ path: 'event.type', category: 'Common' });
  for (const field of ['tenantSlug', 'fingerprint', 'rawPayload', 'originalSeverityString', 'originalStatusString', 'entityId', 'entityType', 'companyId', 'schemaId', 'schemaVersion', 'outputVersion']) {
    for (const root of [`${sourceRoot}.data`, 'event.data']) entries.push({ path: `${root}.${field}`, category: 'Payload' });
  }
  for (const root of [`${sourceRoot}.data.normalisedPayload`, 'event.data.normalisedPayload']) {
    entries.push({ path: root, category: 'Payload' });
    for (const path of payloadPaths) if (validVariablePath(path)) entries.push({ path: `${root}.${path}`, category: 'Payload' });
  }
  for (const path of ['integration.id', 'integration.name', 'integration.provider', 'integration.version'])
    entries.push({ path, category: 'Integration' });
  for (const step of steps.filter(step => step.stepOrder < order)) {
    if (step.stepType === 'RunSubWorkflow' && typeof step.config.alias === 'string' && step.config.alias.trim()) {
      const prefix = `subworkflow.${step.config.alias.trim()}`;
      entries.push({ path: `${prefix}.status`, category: 'Steps', note: 'Completed or Failed status of the preceding sub-workflow call.' });
      entries.push({ path: `${prefix}.resultCode`, category: 'Steps', note: 'Declared business result returned by the preceding sub-workflow.' });
      entries.push({ path: `${prefix}.outputs`, category: 'Steps', note: 'Dynamic outputs returned by Return result; append an output name.' });
      continue;
    }
    for (const field of outputs[step.stepType] ?? []) entries.push({ path: `step.${step.stepOrder}.${field}`, category: 'Steps', note: 'Only when this preceding step executes; some outputs are conditional.' });
    if (step.stepType === 'IntegrationAction') entries.push({ path: 'integration.ticketId', category: 'Integration', note: 'Latest preceding integration action association.' });
    if (step.stepType === 'IntegrationAction' && typeof step.config.responseMappingsJson === 'string') {
      try {
        const mappings = JSON.parse(step.config.responseMappingsJson) as Record<string, unknown>;
        for (const target of Object.keys(mappings)) {
          if (!validVariablePath(target)) continue;
          const category = target.startsWith('alert.') ? 'Alert'
            : target.startsWith('notification.') ? 'Notification'
              : target.startsWith('integration.') ? 'Integration' : 'Steps';
          entries.push({ path: target, category, note: 'Mapped from a preceding outbound action response.' });
        }
      } catch { /* Ignore malformed optional action metadata. */ }
    }
  }
  return Array.from(new Map(entries.map(entry => [entry.path, entry])).values());
}

export function updateMessageContent(config: NotificationStepConfig, field: 'subjectTemplate' | 'bodyTemplate', value: string): NotificationStepConfig {
  return { ...config, [field]: value, ...(field === 'bodyTemplate' && config.bodySourceField ? { bodySourceField: undefined } : {}) };
}

export function insertContentToken(text: string, path: string, start: number, end: number) {
  const token = `{{${path}}}`;
  return { text: text.slice(0, start) + token + text.slice(end), caret: start + token.length };
}

export function expandContent(template: string, variables: Record<string, string>) {
  const lookup = new Map(Object.entries(variables).map(([key, value]) => [key.toLowerCase(), value]));
  const missing = new Set<string>();
  const text = template.replace(/\{\{([a-zA-Z0-9_\-.]+)(?:\|([^}]*))?\}\}/g, (token, key: string, fallback: string | undefined) => {
    if (!/^secret(?:\.|$)/i.test(key)) {
      const value = lookup.get(key.toLowerCase());
      if (value) return value;
    }
    if (fallback !== undefined && !/^secret(?:\.|$)/i.test(key)) return fallback;
    missing.add(key);
    return token;
  });
  return { text, missing: Array.from(missing) };
}

export function schemaContentPaths(text: string): string[] {
  const root = parseJsonObject(text);
  const found = new Set<string>();
  let nodes = 0;
  const visit = (node: unknown, path: string, depth: number, references: string[]) => {
    if (++nodes > 2000 || depth > 16 || !node || typeof node !== 'object' || Array.isArray(node)) return;
    const schema = node as Record<string, unknown>;
    if (typeof schema.$ref === 'string') {
      const reference = schema.$ref;
      if (!reference.startsWith('#/') || references.includes(reference)) return;
      let resolved: unknown = root;
      for (const part of reference.slice(2).split('/').map(part => part.replace(/~1/g, '/').replace(/~0/g, '~'))) {
        if (!resolved || typeof resolved !== 'object' || !Object.prototype.hasOwnProperty.call(resolved, part)) return;
        resolved = (resolved as Record<string, unknown>)[part];
      }
      visit(resolved, path, depth + 1, [...references, reference]);
      return;
    }
    const types = typeof schema.type === 'string' ? [schema.type]
      : Array.isArray(schema.type) ? schema.type.filter((type): type is string => typeof type === 'string') : [];
    if (schema.properties && typeof schema.properties === 'object' && !Array.isArray(schema.properties)) {
      for (const [key, child] of Object.entries(schema.properties)) visit(child, path ? `${path}.${key}` : key, depth + 1, references);
    } else if (types.includes('array')) {
      visit(schema.items, path ? `${path}.0` : '0', depth + 1, references);
    } else if (path && validVariablePath(path)) {
      if (types.some(type => ['string', 'number', 'integer', 'boolean'].includes(type))) found.add(path);
    }
  };
  visit(root, '', 0, []);
  return Array.from(found);
}

export function exampleContentVariables(payloadText: string, source: ContentSource, bindingsText: string, contextText: string) {
  const variables: Record<string, string> = Object.create(null);
  variables['event.type'] = source;
  const paths: string[] = [];
  const prefix = source.toLowerCase();
  const payload = payloadText.trim() ? parseJsonObject(payloadText) : undefined;
  if (payload) {
    const root = JSON.stringify(payload);
    variables['event.data.normalisedPayload'] = root;
    variables[`${prefix}.data.normalisedPayload`] = root;
    const flatten = (value: unknown, path: string) => {
      if (value !== null && typeof value === 'object') {
        for (const [key, child] of Object.entries(value)) flatten(child, path ? `${path}.${key}` : key);
      } else if (validVariablePath(path)) {
        paths.push(path);
        for (const rootPath of ['event.data.normalisedPayload', `${prefix}.data.normalisedPayload`]) {
          variables[`${rootPath}.${path}`] = value === null ? '' : String(value);
        }
      }
    };
    flatten(payload, '');
    const bindings = bindingsText.trim() ? parseJsonObject(bindingsText) : {};
    const names: Record<string, string> = { title: 'title', description: 'description', severity: 'severity', status: 'status',
      service: 'service', source: 'source', sourceEventId: 'externalalertid', correlationId: 'correlationid', occurredUtc: 'occurredat', externalLink: 'externallink' };
    for (const [field, name] of Object.entries(names)) {
      const rule = bindings[field];
      if (!rule || typeof rule !== 'object' || Array.isArray(rule)) continue;
      const binding = rule as Record<string, unknown>;
      if (typeof binding.source !== 'string' || (binding.source !== '' && !binding.source.startsWith('/'))) continue;
      let value: unknown = payload;
      for (const key of binding.source === '' ? [] : binding.source.slice(1).split('/').map(key => key.replace(/~1/g, '/').replace(/~0/g, '~'))) {
        value = value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, key) ? (value as Record<string, unknown>)[key] : undefined;
      }
      if (value === undefined || value === null) value = binding.default;
      if (typeof value === 'string' && value.trim()) {
        variables[`event.${name}`] = value.trim();
        variables[`${prefix}.${name}`] = value.trim();
      }
    }
  }
  if (contextText.trim()) {
    for (const [key, value] of Object.entries(parseJsonObject(contextText))) {
      if (!validVariablePath(key) || key.startsWith(source === 'Alert' ? 'notification.' : 'alert.')) continue;
      if (typeof value !== 'string') throw new Error('Example variable values must be strings.');
      variables[key] = value;
    }
  }
  return { variables, paths };
}